import React, { useState } from 'react';
import { signInAnonymously } from 'firebase/auth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { track } from '../services/analytics';

const FOLLOWER_RANGES = [
  'Under 1,000',
  '1,000 – 5,000',
  '5,000 – 25,000',
  '25,000 – 100,000',
  '100,000+',
];

interface FormState {
  fullName: string;
  email: string;
  cityCountry: string;
  instagram: string;
  tiktok: string;
  followerCount: string;
  upcomingRaces: string;
  whyRally: string;
}

const EMPTY_FORM: FormState = {
  fullName: '',
  email: '',
  cityCountry: '',
  instagram: '',
  tiktok: '',
  followerCount: '',
  upcomingRaces: '',
  whyRally: '',
};

const STEP_TITLES = ['About you', 'Your audience', 'Your running'];

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// Strip a leading @ or a pasted profile URL down to the bare handle.
const normalizeHandle = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const fromUrl = trimmed.match(/(?:instagram\.com|tiktok\.com)\/@?([A-Za-z0-9._-]+)/i);
  return (fromUrl ? fromUrl[1] : trimmed).replace(/^@+/, '');
};

interface AmbassadorApplyFormProps {
  onClose: () => void;
}

const AmbassadorApplyForm: React.FC<AmbassadorApplyFormProps> = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const setField = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validateStep = (index: number): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};

    if (index === 0) {
      if (!form.fullName.trim()) next.fullName = 'Please tell us your name';
      if (!form.email.trim()) next.email = 'Please add your email';
      else if (!isValidEmail(form.email)) next.email = 'That email doesn’t look right';
      if (!form.cityCountry.trim()) next.cityCountry = 'Where are you based?';
    }

    if (index === 1) {
      if (!form.instagram.trim() && !form.tiktok.trim()) {
        next.instagram = 'Add at least one handle so we can find you';
      }
      if (!form.followerCount) next.followerCount = 'Pick the closest range';
    }

    if (index === 2) {
      if (!form.upcomingRaces.trim()) next.upcomingRaces = 'Tell us what you’re running';
      if (!form.whyRally.trim()) next.whyRally = 'We’d love to hear why';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    track('ambassador_apply_step_completed', { step: step + 1 });
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (step === 0) {
      onClose();
      return;
    }
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(2)) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      // Anonymous auth only — applicants don't need a Rally account to apply.
      if (!auth.currentUser) await signInAnonymously(auth);

      await addDoc(collection(db, 'ambassadorApplications'), {
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        cityCountry: form.cityCountry.trim(),
        instagram: normalizeHandle(form.instagram),
        tiktok: normalizeHandle(form.tiktok),
        followerCount: form.followerCount,
        upcomingRaces: form.upcomingRaces.trim(),
        whyRally: form.whyRally.trim(),
        status: 'new',
        emailSent: false,
        timestamp: serverTimestamp(),
      });

      track('ambassador_application_submitted', { follower_count: form.followerCount });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting ambassador application:', err);
      setSubmitError('Something went wrong. Please try again, or email hello@runwithrally.com.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="amb-form-overlay">
        <div className="amb-form-card amb-form-card--success">
          <div className="amb-success-mark">✓</div>
          <h2 className="amb-form-title">Application received</h2>
          <p className="amb-form-sub">
            Thanks, {form.fullName.trim().split(/\s+/)[0]}. We review applications every week and
            email everyone either way — keep an eye on {form.email.trim().toLowerCase()}.
          </p>
          <button className="amb-btn amb-btn--primary" onClick={onClose}>
            Back to the page
          </button>
        </div>
      </div>
    );
  }

  const isLastStep = step === 2;

  return (
    <div className="amb-form-overlay">
      <div className="amb-form-card">
        <div className="amb-form-head">
          <button className="amb-form-back" onClick={handleBack} type="button" aria-label="Back">
            ←
          </button>
          <div className="amb-form-progress">
            {STEP_TITLES.map((title, i) => (
              <div
                key={title}
                className={`amb-progress-dot${i <= step ? ' amb-progress-dot--done' : ''}`}
              />
            ))}
          </div>
          <button className="amb-form-close" onClick={onClose} type="button" aria-label="Close">
            ✕
          </button>
        </div>

        <p className="amb-form-step">Step {step + 1} of 3</p>
        <h2 className="amb-form-title">{STEP_TITLES[step]}</h2>

        <form onSubmit={isLastStep ? handleSubmit : e => { e.preventDefault(); handleNext(); }}>
          {step === 0 && (
            <>
              <label className="amb-field">
                <span className="amb-label">Full name</span>
                <input
                  className={`amb-input${errors.fullName ? ' amb-input--error' : ''}`}
                  value={form.fullName}
                  onChange={setField('fullName')}
                  placeholder="Jordan Reyes"
                  autoFocus
                />
                {errors.fullName && <span className="amb-error">{errors.fullName}</span>}
              </label>

              <label className="amb-field">
                <span className="amb-label">Email</span>
                <input
                  className={`amb-input${errors.email ? ' amb-input--error' : ''}`}
                  type="email"
                  value={form.email}
                  onChange={setField('email')}
                  placeholder="you@email.com"
                />
                {errors.email && <span className="amb-error">{errors.email}</span>}
              </label>

              <label className="amb-field">
                <span className="amb-label">City &amp; country</span>
                <input
                  className={`amb-input${errors.cityCountry ? ' amb-input--error' : ''}`}
                  value={form.cityCountry}
                  onChange={setField('cityCountry')}
                  placeholder="Chicago, USA"
                />
                {errors.cityCountry && <span className="amb-error">{errors.cityCountry}</span>}
              </label>
            </>
          )}

          {step === 1 && (
            <>
              <label className="amb-field">
                <span className="amb-label">
                  Instagram <span className="amb-label-hint">handle or link</span>
                </span>
                <input
                  className={`amb-input${errors.instagram ? ' amb-input--error' : ''}`}
                  value={form.instagram}
                  onChange={setField('instagram')}
                  placeholder="@yourhandle"
                  autoFocus
                />
                {errors.instagram && <span className="amb-error">{errors.instagram}</span>}
              </label>

              <label className="amb-field">
                <span className="amb-label">
                  TikTok <span className="amb-label-hint">handle or link</span>
                </span>
                <input
                  className="amb-input"
                  value={form.tiktok}
                  onChange={setField('tiktok')}
                  placeholder="@yourhandle"
                />
              </label>

              <label className="amb-field">
                <span className="amb-label">Combined followers</span>
                <select
                  className={`amb-input amb-select${errors.followerCount ? ' amb-input--error' : ''}`}
                  value={form.followerCount}
                  onChange={setField('followerCount')}
                >
                  <option value="">Select a range</option>
                  {FOLLOWER_RANGES.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
                {errors.followerCount && <span className="amb-error">{errors.followerCount}</span>}
              </label>

              <p className="amb-form-note">
                Follower count isn’t a filter — we’ve taken ambassadors with a few hundred
                followers and a very loud run club.
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <label className="amb-field">
                <span className="amb-label">
                  Races you’re running in the next 12 months
                </span>
                <textarea
                  className={`amb-input amb-textarea${errors.upcomingRaces ? ' amb-input--error' : ''}`}
                  value={form.upcomingRaces}
                  onChange={setField('upcomingRaces')}
                  placeholder="Chicago Marathon (Oct), Houston Half (Jan)…"
                  rows={3}
                  autoFocus
                />
                {errors.upcomingRaces && <span className="amb-error">{errors.upcomingRaces}</span>}
              </label>

              <label className="amb-field">
                <span className="amb-label">Why do you want to be a Rally ambassador?</span>
                <textarea
                  className={`amb-input amb-textarea${errors.whyRally ? ' amb-input--error' : ''}`}
                  value={form.whyRally}
                  onChange={setField('whyRally')}
                  placeholder="A few sentences is plenty."
                  rows={5}
                />
                {errors.whyRally && <span className="amb-error">{errors.whyRally}</span>}
              </label>
            </>
          )}

          {submitError && <p className="amb-submit-error">{submitError}</p>}

          <button
            className="amb-btn amb-btn--primary amb-btn--full"
            type="submit"
            disabled={submitting}
          >
            {submitting ? <span className="amb-btn-spinner" /> : isLastStep ? 'Submit application' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AmbassadorApplyForm;
